import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FileText, Download, X, ExternalLink, FileSpreadsheet, FileText as FileTextIcon, File, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import * as XLSX from 'xlsx';
import { API_URL } from '../../utils/ConfigUtils';

interface DocumentViewerProps {
  documentUrl: string;
  documentName?: string;
  isOpen: boolean;
  onClose: () => void;
}

// Performance configuration
const PERFORMANCE_CONFIG = {
  // File size thresholds (in bytes)
  LARGE_FILE_THRESHOLD: 5 * 1024 * 1024, // 5MB
  HUGE_FILE_THRESHOLD: 25 * 1024 * 1024, // 25MB
  
  // Chunk sizes for large files
  CHUNK_SIZE: 1024 * 1024, // 1MB chunks
  
  // Timeout for large file operations
  LARGE_FILE_TIMEOUT: 30000, // 30 seconds
  
  // Memory management
  MAX_MEMORY_USAGE: 100 * 1024 * 1024, // 100MB
};

const DocumentViewer: React.FC<DocumentViewerProps> = ({
  documentUrl,
  documentName,
  isOpen,
  onClose,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [excelData, setExcelData] = useState<any[] | null>(null);
  const [docxContent, setDocxContent] = useState<string | null>(null);
  const [fileType, setFileType] = useState<'pdf' | 'csv' | 'excel' | 'word' | 'unknown'>('unknown');
  
  // Performance states
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [loadingProgress, setLoadingProgress] = useState<number>(0);
  const [isLargeFile, setIsLargeFile] = useState<boolean>(false);
  const [loadingStrategy, setLoadingStrategy] = useState<'normal' | 'chunked' | 'streaming'>('normal');
  
  // Refs for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // DocumentViewer component called
  
  // Extract filename from URL if documentName is not provided
  const fileName = documentName || documentUrl.split('/').pop() || 'document.pdf';
      // Document fileName extracted
  
  // Helper function to construct backend URL dynamically - SIMPLIFIED
  const getBackendUrl = (path: string) => {
    // If it's already a full URL, return as is
    if (path.startsWith('http')) {
      return path;
    }
    
    // For relative paths, construct the full URL using API_URL
    return `${API_URL}${path}`;
  };
  
  // Debug: Log the constructed URLs
  const fullBackendUrl = getBackendUrl(documentUrl);
      // DocumentViewer URL processing

  // Check file size and determine loading strategy
  const checkFileSize = async (url: string): Promise<number> => {
    try {
      // Try HEAD request first (most efficient)
      const response = await fetch(url, { method: 'HEAD' });
      if (response.ok) {
        const contentLength = response.headers.get('content-length');
        const size = contentLength ? parseInt(contentLength, 10) : 0;
        // File size determined via HEAD request
        return size;
      }
      
      // If HEAD fails, try GET with range request (more compatible)
      const rangeResponse = await fetch(url, { 
        headers: { 'Range': 'bytes=0-1023' } // Request first 1KB
      });
      
      if (rangeResponse.ok) {
        const contentRange = rangeResponse.headers.get('content-range');
        if (contentRange) {
          const totalSize = contentRange.split('/')[1];
          const size = parseInt(totalSize, 10);
          console.log(`File size (Range): ${(size / 1024 / 1024).toFixed(2)} MB`);
          return size;
        }
      }
      
      // Fallback: estimate size based on file extension and type
      console.log('Could not determine file size, using fallback estimation');
      return 0; // Will use default loading strategy
      
    } catch (error) {
      console.warn('Could not determine file size:', error);
      return 0; // Will use default loading strategy
    }
  };

  // Determine file type based on file extension
  useEffect(() => {
    const extension = fileName.toLowerCase().split('.').pop();
    if (extension === 'pdf') {
      setFileType('pdf');
    } else if (extension === 'csv') {
      setFileType('csv');
    } else if (['xls', 'xlsx'].includes(extension || '')) {
      setFileType('excel');
    } else if (['doc', 'docx'].includes(extension || '')) {
      setFileType('word');
    } else {
      setFileType('unknown');
    }
  }, [fileName]);

  // Optimized CSV loading with streaming for large files
  const loadCsvContentOptimized = async (url: string, size: number) => {
    // If we can't determine size, use standard loading as fallback
    if (size === 0) {
      console.log('Size unknown, using standard CSV loading');
      setLoadingStrategy('normal');
      await loadCsvStandard(url);
      return;
    }
    
    if (size > PERFORMANCE_CONFIG.HUGE_FILE_THRESHOLD) {
      // For huge files, show warning and suggest download
      setError('File is too large for preview. Please download to view.');
      setIsLoading(false);
      return;
    }

    if (size > PERFORMANCE_CONFIG.LARGE_FILE_THRESHOLD) {
      // For large files, use chunked loading
      setLoadingStrategy('chunked');
      await loadCsvChunked(url, size);
    } else {
      // For normal files, use standard loading
      setLoadingStrategy('normal');
      await loadCsvStandard(url);
    }
  };

  // Standard CSV loading
  const loadCsvStandard = async (url: string) => {
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch CSV file');
      
      const content = await response.text();
      setCsvContent(content);
    } catch (error) {
      console.error('Error loading CSV:', error);
      setError('Failed to load CSV file for preview');
    } finally {
      setIsLoading(false);
    }
  };

  // Chunked CSV loading for large files
  const loadCsvChunked = async (url: string, size: number) => {
    try {
      const chunks: string[] = [];
      const totalChunks = Math.ceil(size / PERFORMANCE_CONFIG.CHUNK_SIZE);
      
      for (let i = 0; i < totalChunks; i++) {
        const start = i * PERFORMANCE_CONFIG.CHUNK_SIZE;
        const end = Math.min(start + PERFORMANCE_CONFIG.CHUNK_SIZE - 1, size - 1);
        
        const response = await fetch(url, {
          headers: { 'Range': `bytes=${start}-${end}` }
        });
        
        if (!response.ok) throw new Error('Failed to fetch CSV chunk');
        
        const chunk = await response.text();
        chunks.push(chunk);
        
        // Update progress
        const progress = ((i + 1) / totalChunks) * 100;
        setLoadingProgress(progress);
        
        // Small delay to prevent UI blocking
        await new Promise(resolve => setTimeout(resolve, 10));
      }
      
      const fullContent = chunks.join('');
      setCsvContent(fullContent);
    } catch (error) {
      console.error('Error loading CSV chunks:', error);
      setError('Failed to load large CSV file. Please download to view.');
    } finally {
      setIsLoading(false);
      setLoadingProgress(0);
    }
  };

  // Optimized Excel loading with memory management
  const loadExcelContentOptimized = async (url: string, size: number) => {
    // If we can't determine size, use standard loading as fallback
    if (size === 0) {
      console.log('Size unknown, using standard Excel loading');
      // Use the existing Excel loading logic without size optimization
      try {
        setIsLoading(true);
        const fullUrl = getBackendUrl(documentUrl);
        
        const response = await fetch(fullUrl);
        if (!response.ok) throw new Error('Failed to fetch Excel file');
        
        const arrayBuffer = await response.arrayBuffer();
        const workbook = XLSX.read(arrayBuffer, { type: 'array' });
        
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];
        
        if (jsonData.length > 0) {
          const headers = jsonData[0] as string[];
          const data = jsonData.slice(1).map((row: unknown[]) => {
            const obj: any = {};
            headers.forEach((header, index) => {
              obj[header] = (row as any[])[index] || '';
            });
            return obj;
          });
          setExcelData(data);
        } else {
          setExcelData([]);
        }
              } catch (error) {
          if (error instanceof Error) {
            console.error('Error loading Excel:', error);
            setError(`Failed to load Excel file: ${error.message}`);
          } else {
            console.error('Error loading Excel:', error);
            setError('Failed to load Excel file for preview');
          }
        } finally {
          setIsLoading(false);
        }
        return;
      }
      
      if (size > PERFORMANCE_CONFIG.HUGE_FILE_THRESHOLD) {
        setError('Excel file is too large for preview. Please download to view.');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const fullUrl = getBackendUrl(documentUrl);
        
        // Create abort controller for timeout
        abortControllerRef.current = new AbortController();
        
        // Set timeout for large files
        if (size > PERFORMANCE_CONFIG.LARGE_FILE_THRESHOLD) {
          loadingTimeoutRef.current = setTimeout(() => {
            abortControllerRef.current?.abort();
            setError('Loading timeout. File may be too large for preview.');
            setIsLoading(false);
          }, PERFORMANCE_CONFIG.LARGE_FILE_TIMEOUT);
        }

        const response = await fetch(fullUrl, {
          signal: abortControllerRef.current.signal
        });
        
        if (!response.ok) throw new Error('Failed to fetch Excel file');
        
        const arrayBuffer = await response.arrayBuffer();
        
        // Clear timeout
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }

        // Process Excel with memory optimization
        const workbook = XLSX.read(arrayBuffer, { 
          type: 'array',
          cellFormula: false, // Disable formula parsing for performance
          cellStyles: false,  // Disable style parsing for performance
          cellDates: false    // Disable date parsing for performance
        });
        
        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        
        // Convert to JSON with row limit for very large sheets
        const maxRows = size > PERFORMANCE_CONFIG.LARGE_FILE_THRESHOLD ? 1000 : 10000;
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1,
          range: maxRows // Limit rows for large files
        }) as unknown[][];
        
        // Convert array format to object format for easier rendering
        if (jsonData.length > 0) {
          const headers = jsonData[0] as string[];
          const data = jsonData.slice(1).map((row: unknown[]) => {
            const obj: any = {};
            headers.forEach((header, index) => {
              obj[header] = (row as any[])[index] || '';
            });
            return obj;
          });
          setExcelData(data);
          
          // Show warning if data was truncated
          if (jsonData.length >= maxRows) {
            setError(`Showing first ${maxRows} rows. File may be truncated for performance.`);
          }
        } else {
          setExcelData([]);
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          setError('Loading cancelled due to timeout.');
        } else {
          console.error('Error loading Excel:', error);
          setError('Failed to load Excel file for preview');
        }
      } finally {
        setIsLoading(false);
        // Cleanup
        if (loadingTimeoutRef.current) {
          clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      }
    };

  // Load CSV and Excel content for preview with performance optimization
  useEffect(() => {
    if (!documentUrl) return;

    const loadContent = async () => {
      try {
        const fullUrl = getBackendUrl(documentUrl);
        console.log('Attempting to load document from:', fullUrl);
        
        // Skip file size checking for immediate loading - this was causing 405 errors
        console.log('Skipping file size check for immediate loading');
        setFileSize(0);
        setIsLargeFile(false);
        
        if (fileType === 'csv') {
          // Direct CSV loading without size optimization
          try {
            setIsLoading(true);
            const response = await fetch(fullUrl);
            if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            
            const content = await response.text();
            setCsvContent(content);
            setLoadingStrategy('normal');
          } catch (error) {
            console.error('Error loading CSV:', error);
            setError('Failed to load CSV file for preview');
          } finally {
            setIsLoading(false);
          }
        } else if (fileType === 'excel') {
          // Direct Excel loading without size optimization
          try {
            setIsLoading(true);
            const response = await fetch(fullUrl);
            if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            
            const arrayBuffer = await response.arrayBuffer();
            const workbook = XLSX.read(arrayBuffer, { type: 'array' });
            
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as unknown[][];
            
            if (jsonData.length > 0) {
              const headers = jsonData[0] as string[];
              const data = jsonData.slice(1).map((row: unknown[]) => {
                const obj: any = {};
                headers.forEach((header, index) => {
                  obj[header] = (row as any[])[index] || '';
                });
                return obj;
              });
              setExcelData(data);
            } else {
              setExcelData([]);
            }
          } catch (error) {
            console.error('Error loading Excel:', error);
            setError('Failed to load Excel file for preview');
          } finally {
            setIsLoading(false);
          }
        } else if (fileType === 'word') {
          // Word document loading with better error handling
          try {
            setIsLoading(true);
            console.log('Loading Word document...');
            
            const response = await fetch(fullUrl);
            console.log('Word document response status:', response.status);
            
            if (!response.ok) {
              throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            
            const textContent = await response.text();
            console.log('Word document loaded, content length:', textContent.length);
            setDocxContent(textContent);
          } catch (error) {
            console.error('Error loading Word document:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            setError(`Failed to load Word document: ${errorMessage}. Please download to view.`);
          } finally {
            setIsLoading(false);
          }
        } else if (fileType === 'pdf') {
          console.log('PDF file detected, no content loading needed');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error in content loading:', error);
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        setError(`Failed to load document: ${errorMessage}`);
        setIsLoading(false);
      }
    };

    loadContent();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, [fileType, documentUrl]);
  
  if (!isOpen) {
    console.log('DocumentViewer not open, returning null');
    return null;
  }

  const handleDownload = () => {
    // If it's a data URL, create a download link
    if (documentUrl.startsWith('data:')) {
      const link = document.createElement('a');
      link.href = documentUrl;
      link.download = fileName || 'document.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      // For regular URLs, ensure we have the full backend URL and download
      const fullUrl = getBackendUrl(documentUrl);
      const link = document.createElement('a');
      link.href = fullUrl;
      link.download = fileName || 'document.pdf';
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const handleLoad = () => {
    setIsLoading(false);
  };

  const handleError = () => {
    setIsLoading(false);
    setError('Failed to load the document. Please try again later.');
  };

  // Simple loading indicator
  const renderLoadingState = () => (
    <div className="flex items-center justify-center h-full">
      <div className="text-center">
        <Loader2 className="animate-spin h-12 w-12 text-blue-400 mx-auto mb-4" />
        <p className="text-gray-400 mb-2">Loading document...</p>
        <p className="text-sm text-gray-500">Please wait while we process your file</p>
      </div>
    </div>
  );

  // Helper function to parse CSV content and render as table
  const renderCsvContent = (content: string) => {
    const lines = content.split('\n').filter(line => line.trim());
    if (lines.length === 0) {
      return <div className="text-gray-400 text-center py-8">Empty CSV file</div>;
    }

    // Simple CSV parser that handles quoted fields
    const parseCsvLine = (line: string) => {
      const result = [];
      let current = '';
      let inQuotes = false;
      
      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      
      result.push(current.trim());
      return result.map(field => field.replace(/^"|"$/g, '')); // Remove surrounding quotes
    };

    const headers = parseCsvLine(lines[0]);
    const data = lines.slice(1).map(line => parseCsvLine(line));

    return (
      <div className="overflow-auto max-h-[70vh]">
        <table className="min-w-full bg-background-card border-collapse">
          <thead className="bg-slate-700 sticky top-0">
            <tr>
              {headers.map((header, index) => (
                <th key={index} className="px-4 py-2 text-left text-sm font-medium text-gray-200 border-b border-border-dark">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-slate-700/50">
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="px-4 py-2 text-sm text-gray-300 border-b border-border-dark">
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Helper function to render Excel content as table
  const renderExcelContent = (data: any[]) => {
    if (!data || data.length === 0) {
      return <div className="text-gray-400 text-center py-8">Empty Excel file</div>;
    }

    // Get headers from first row
    const headers = Object.keys(data[0]);
    if (headers.length === 0) {
      return <div className="text-gray-400 text-center py-8">No data found in Excel file</div>;
    }

    return (
      <div className="overflow-auto max-h-[70vh]">
        <table className="min-w-full bg-background-card border-collapse">
          <thead className="bg-slate-700 sticky top-0">
            <tr>
              {headers.map((header, index) => (
                <th key={index} className="px-4 py-2 text-left text-sm font-medium text-gray-200 border-b border-border-dark">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-slate-700/50">
                {headers.map((header, cellIndex) => (
                  <td key={cellIndex} className="px-4 py-2 text-sm text-gray-300 border-b border-border-dark">
                    {row[header] || ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Helper function to render DOCX content
  const renderDocxContent = (content: string) => {
    if (!content || content.trim() === '') {
      return <div className="text-gray-400 text-center py-8">Empty Word document</div>;
    }

    // Split content into paragraphs and render with proper spacing
    const paragraphs = content.split('\n').filter(p => p.trim());
    
    return (
      <div className="overflow-auto max-h-[70vh] p-4">
        <div className="prose prose-invert max-w-none">
          {paragraphs.map((paragraph, index) => (
            <p key={index} className="text-gray-300 mb-4 leading-relaxed">
              {paragraph}
            </p>
          ))}
        </div>
      </div>
    );
  };

  // Helper function to render file type specific content
  const renderFileContent = () => {
    switch (fileType) {
      case 'pdf':
        return (
          <iframe
            src={documentUrl.startsWith('data:') ? documentUrl : `${getBackendUrl(documentUrl)}#toolbar=0`}
            className="w-full h-full min-h-[70vh]"
            onLoad={handleLoad}
            onError={handleError}
            title="Document Preview"
          />
        );
      case 'csv':
        if (isLoading) {
          return renderLoadingState();
        }
        if (csvContent) {
          return renderCsvContent(csvContent);
        }
        if (error) {
          return (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FileTextIcon size={48} className="text-red-400 mx-auto mb-4" />
                <p className="text-red-400">Failed to load CSV file</p>
                <p className="text-sm text-gray-500">{error}</p>
              </div>
            </div>
          );
        }
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FileTextIcon size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">No CSV data available</p>
              <p className="text-sm text-gray-500">Please try refreshing or contact support</p>
            </div>
          </div>
        );
      case 'excel':
        if (isLoading) {
          return renderLoadingState();
        }
        if (excelData) {
          return renderExcelContent(excelData);
        }
        if (error) {
          return (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <FileSpreadsheet size={48} className="text-red-400 mx-auto mb-4" />
                <p className="text-red-400 mb-4">Failed to load Excel file</p>
                <p className="text-sm text-gray-500">{error}</p>
              </div>
            </div>
          );
        }
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FileSpreadsheet size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">No Excel data available</p>
              <p className="text-sm text-gray-500">Please try refreshing or contact support</p>
            </div>
          </div>
        );
      case 'word':
        if (isLoading) {
          return renderLoadingState();
        }
        if (docxContent) {
          return renderDocxContent(docxContent);
        }
        if (error) {
          return (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <File size={48} className="text-red-400 mx-auto mb-4" />
                <p className="text-red-400 mb-4">Failed to load Word document</p>
                <p className="text-sm text-gray-500">{error}</p>
              </div>
            </div>
          );
        }
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <File size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">No Word document content available</p>
              <p className="text-sm text-gray-500">Please try refreshing or contact support</p>
            </div>
          </div>
        );
      default:
        return (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <FileText size={48} className="text-gray-400 mx-auto mb-4" />
              <p className="text-gray-400">Unsupported file type</p>
              <p className="text-sm text-gray-500">Please download the file to view its contents</p>
            </div>
          </div>
        );
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div 
        className="bg-background-card border border-border-dark rounded-lg p-4 max-w-6xl w-[95%] max-h-[95vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4 px-2">
          <div className="flex items-center">
            {fileType === 'csv' ? (
              <FileTextIcon size={20} className="text-primary mr-2" />
            ) : fileType === 'excel' ? (
              <FileSpreadsheet size={20} className="text-primary mr-2" />
            ) : fileType === 'word' ? (
              <File size={20} className="text-primary mr-2" />
            ) : (
            <FileText size={20} className="text-primary mr-2" />
            )}
            <div>
            <h2 className="text-lg font-semibold truncate max-w-md">{fileName}</h2>
              
              {/* Performance information panel */}
              {fileSize && (
                <div className="flex items-center space-x-4 mt-1 text-xs text-gray-400">
                  <span>Size: {(fileSize / 1024 / 1024).toFixed(2)} MB</span>
                  
                  {isLargeFile && (
                    <span className="text-yellow-400">
                      ⚠️ Large file - optimized loading
                    </span>
                  )}
                  
                  {loadingStrategy === 'chunked' && (
                    <span className="text-blue-400">
                      🔄 Chunked loading
                    </span>
                  )}
                  
                  {fileType === 'excel' && isLargeFile && (
                    <span className="text-orange-400">
                      📊 Row limit: 1,000 for performance
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleDownload}
              className="flex items-center"
            >
              <Download size={16} className="mr-1" />
              Download
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                const fullUrl = getBackendUrl(documentUrl);
                window.open(fullUrl, '_blank');
              }}
              className="flex items-center"
            >
              <ExternalLink size={16} className="mr-1" />
              Open
            </Button>
            <button 
              onClick={onClose}
              className="p-1 rounded-full hover:bg-background-hover text-text-muted-dark"
            >
              <X size={18} />
            </button>
          </div>
        </div>
        
        <div className="flex-1 min-h-[75vh] border border-border-dark rounded-lg overflow-hidden relative">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background-card">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          )}
          
          {error ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-background-card">
              <FileText size={40} className="text-red-500 mb-2" />
              <p className="text-red-500">{error}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-4"
                onClick={handleDownload}
              >
                Download Instead
              </Button>
            </div>
          ) : (
            <div className="w-full h-full min-h-[70vh] bg-background-card">
              {renderFileContent()}
            </div>
          )}
        </div>
        
        {/* Performance tips for large files */}
        {isLargeFile && !isLoading && !error && (
          <div className="mt-4 p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg">
            <div className="flex items-start space-x-2">
              <div className="text-blue-400 mt-0.5">💡</div>
              <div className="text-sm">
                <p className="text-blue-300 font-medium mb-1">Performance Tips for Large Files:</p>
                <ul className="text-blue-200/80 text-xs space-y-1">
                  <li>• Large files use optimized loading strategies for better performance</li>
                  <li>• Excel files over 25MB are limited to first 1,000 rows for preview</li>
                  <li>• CSV files use chunked loading to prevent browser freezing</li>
                  <li>• Consider downloading very large files for full access</li>
                  <li>• Close other browser tabs to free up memory</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
  
  // Use createPortal to render the modal directly in the document body
  return createPortal(modalContent, document.body);
};

export default DocumentViewer;